import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MATheology = () => {
  const coreCourses = [
    { code: 'THEO 500', name: 'Introduction to Biblical Theology', credits: 3 },
    { code: 'THEO 501', name: 'Historical Theology', credits: 3 },
    { code: 'THEO 502', name: 'The Doctrines of God, Man and Sin', credits: 3 },
    { code: 'THEO 503', name: 'The Doctrines of Christ, the Holy Spirit, Salvation and End Times', credits: 3 },
    { code: 'THEO 504', name: 'Theology and Philosophy', credits: 3 },
    { code: 'THEO 505', name: 'Contemporary Theology', credits: 3 },
    { code: 'THEO 507', name: 'Wesleyanism: A Theology of Love', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Systematic Theology",
      description: "Comprehensive study of Christian doctrine and theological systems",
      icon: "📚"
    },
    {
      title: "Historical Analysis",
      description: "Understanding theological development through church history",
      icon: "⏳"
    },
    {
      title: "Contemporary Issues",
      description: "Engaging modern theological challenges and developments",
      icon: "🌍"
    },
    {
      title: "Practical Application",
      description: "Connecting theological insights with ministry practice",
      icon: "🤝"
    }
  ];

  const resources = [
    {
      title: "Theological Library",
      description: "Access to extensive theological resources and databases",
      path: "/academics/resources/theology_library"
    },
    {
      title: "Research Center",
      description: "Advanced theological research facilities and support",
      path: "/academics/resources/research_center"
    },
    {
      title: "Ministry Integration",
      description: "Resources for applying theology in practical ministry",
      path: "/academics/resources/ministry_integration"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Theology
            </h1>
            <p className="text-xl opacity-90">
              Advanced study in systematic, historical, and contemporary theology
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The MA in Theology program provides comprehensive training in theological studies, 
                combining historical understanding with contemporary application. This program 
                equips students with the tools to engage in advanced theological research and 
                apply theological insights in various ministry contexts.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (21 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to theological research and ministry.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MATheology; 